import { useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import { toArabicDigits, formatDateYYYYMMDD } from '../../../utils/numbers';

const DiscussionCard = ({ discussion, onClick, isBookmarked = false, onBookmarkToggle, isAuthenticated = false }) => {
  const navigate = useNavigate();
  const getCategoryColor = (category) => {
    // Normalize category name (remove 'دائرة' if present) to match keys
    const normalizedCategory = category?.replace('دائرة ', '') || '';

    const colors = {
      'النفس': '#7D8471',
      'الأدب': '#8B6914',
      'الشعر': '#B8860B',
      'العقل': '#6B5B47',
      'العين': '#D4AF37',
      'الصوت': '#8B7355',
      'التراث': '#8B4513',
      'التصوف': '#5A5A5A',
      'الفنون': '#A0826D',
      'اللسان': '#6B8E23',
      'دائرة الرحلة': '#9B7653',
      'دائرة الدائرة': '#5A5A5A'
    };
    // Fallback to a hex color so appending '4D' works, instead of var()
    return colors?.[normalizedCategory] || '#C5A059';
  };

  const handleBookmarkClick = (e) => {
    e?.stopPropagation();
    if (onBookmarkToggle && isAuthenticated) {
      onBookmarkToggle(discussion?.id);
    }
  };

  return (
    <article
      onClick={onClick}
      className="group bg-[var(--color-background)] overflow-hidden transition-all duration-300 flex flex-col h-full cursor-pointer"
    >
      {/* Image container - same size as ArticleCard */}
      <div className="w-full flex items-center justify-center p-4 lg:p-6 lg:mr-3">
        <div className="relative w-full max-w-[200px] lg:max-w-[13rem]">
          <div className="relative w-full aspect-square rounded-full overflow-hidden levitation-shadow">
            <Image
              src={discussion?.initiator?.avatar}
              alt={discussion?.initiator?.avatarAlt}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              type="user"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent mr-2.5" />
          </div>

          {/* حلقة نقاش + Bookmark — top left of image (same as ArticleCard) */}
          <div className="absolute top-1 left-0 z-10 flex flex-col items-start gap-2">
            <div
              className="px-3 py-1.5 rounded-full backdrop-blur-sm shadow-warm"
              style={{ backgroundColor: `${getCategoryColor(discussion?.category)}4D` }}
            >
              <span
                className="text-[10px] lg:text-xs font-bold text-white"
                style={{ fontFamily: 'Amiri, serif' }}
              >
                حلقة نقاش
              </span>
            </div>
            <button
              onClick={handleBookmarkClick}
              className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-2 rounded-full backdrop-blur-sm bg-white/90 hover:bg-white shadow-warm flex items-center justify-center"
              aria-label="حفظ الحلقة"
            >
              <Icon
                name="Bookmark"
                size={16}
                color={isBookmarked ? getCategoryColor(discussion?.category) : 'var(--color-muted-foreground)'}
                fill={isBookmarked ? getCategoryColor(discussion?.category) : 'none'}
              />
            </button>
          </div>

          {/* Category Pill - Top Right (same as ArticleCard) */}
          <div className="absolute top-1 right-0 z-10 flex flex-col items-end gap-2">
            <div
              className="flex items-center gap-2 px-3 lg:px-4 py-1.5 lg:py-2 rounded-full backdrop-blur-sm shadow-warm"
              style={{ backgroundColor: `${getCategoryColor(discussion?.category)}4D` }}
            >
              <Icon name="Circle" size={6} color="white" fill="white" />
              <span
                className="text-xs lg:text-sm font-bold text-white whitespace-nowrap"
                style={{ fontFamily: 'Amiri, serif', fontWeight: 700 }}
              >
                {discussion?.category}
              </span>
            </div>
          </div>

          {/* Stats at Bottom Left (same position as ArticleCard) */}
          <div className="absolute bottom-1 left-0 flex items-center gap-2 z-10">
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur-sm shadow-warm">
              <Icon name="MessageCircle" size={14} color="var(--color-foreground)" />
              <span
                className="text-xs font-medium text-[var(--color-foreground)]"
                style={{ fontFamily: 'Amiri, serif', fontWeight: 400 }}
              >
                {toArabicDigits(discussion?.participants)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section - same padding as ArticleCard */}
      <div className="flex flex-col flex-1 p-4 lg:p-6 text-center">
        <h3
          className="text-[22px] font-bold text-[var(--color-foreground)] mt-4 lg:mt-6 mb-3 line-clamp-2 group-hover:text-[var(--color-primary)] transition-colors"
          style={{ fontFamily: 'Amiri, serif', fontWeight: 700 }}
        >
          {discussion?.question}
        </h3>

        {/* Initiator Info - same layout as ArticleCard author */}
        <div
          className="flex items-center justify-center gap-3 pt-4 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            if (discussion?.initiator?.id) {
              navigate(`/user/${discussion.initiator.id}`);
            }
          }}
        >
          <Image
            src={discussion?.initiator?.avatar}
            alt={discussion?.initiator?.avatarAlt}
            className="w-10 h-10 rounded-full object-cover levitation-shadow"
            type="user"
          />
          <div className="flex flex-col items-start">
            <span
              className="text-sm font-medium text-[var(--color-foreground)] hover:text-[var(--color-primary)] transition-colors"
              style={{ fontFamily: 'Amiri, serif', fontWeight: 700 }}
            >
              {discussion?.initiator?.name}
            </span>
            <span
              className="text-xs"
              style={{ fontFamily: 'Amiri, serif', fontWeight: 400, color: '#999' }}
              dir="ltr"
            >
              {formatDateYYYYMMDD(discussion?.startedDate ?? '')}
            </span>
          </div>
        </div>
      </div>
    </article>
  );
};

export default DiscussionCard;